import Big from "big.js";
import type { ValidationIssue } from "../index";
import type { Translator } from "../til-translation";

// The file of the items to the ERP (spec v0.3 §7.3, C-40, ADR-253 point 4, ADR-257): how Business Central creates and
// updates its items while their import is by hand, and the physical form of I-01 and I-22. Pure, no DB and no clock:
// the rows in, the text of the file out. The catalog writes it; TIL may import it once it writes the files (D-23,
// T-76). Every rule of the format is here, the executable specification of the file.

/** #13 Product Type: the values of the column, which the reference data and the styles give (V-16). */
export { type ErpProductType, erpProductTypes } from "../reference/enums";

/** The two sends of the file (D-22, closed on 17/10): with the linesheet the EAN may still be missing, not later. */
const erpItemSends = ["linesheet", "attributes"] as const;
export type ErpItemSend = (typeof erpItemSends)[number];

/** Rows of a file at most (§7.3.1): beyond them, more files, each with its own outcome. */
export const ERP_ITEM_FILE_MAX_ROWS = 50_000;

/** The value of each column of a row, in the codes of Business Central: null is an empty column. */
export interface ErpItem {
  /** #1 Product ID: the `skuCode`, the key of the item on Business Central (B-71, D-21). */
  productId: string | null;
  /** #2 EAN CODE. */
  ean: string | null;
  /** #3 Season of the linesheet. */
  season: string | null;
  /** #4 MODEL: Tomorrow's style code. */
  model: string | null;
  /** #7 COLOR: Tomorrow's colour code. */
  color: string | null;
  /** #8 SIZE. */
  size: string | null;
  /** #9 SEX: the gender. */
  sex: string | null;
  /** #10 Brand Name. */
  brandName: string | null;
  /** #11 Brand Code: two characters on the PIM page (V-17). */
  brandCode: string | null;
  /** #12 Wholesale Price, a decimal with a point: the file writes it with a comma (V-15). */
  wholesalePrice: string | null;
  /** #13 Product Type (V-16). */
  productType: string | null;
  /** #14 Made In, ISO 3166-1 alpha-2. */
  madeIn: string | null;
  /** #15 Materials: the composition, in full. */
  materials: string | null;
  /** #16 Product description: the style name. */
  description: string | null;
  /** #17 Category. */
  category: string | null;
  /** #18 Sub-category. */
  subcategory: string | null;
  /** #19 Supplier Name: the supplier of the item, never its manufacturer (V-12). */
  supplierName: string | null;
  /** #20 Supplier Code: the number Business Central gave the supplier, empty until it has one. */
  supplierCode: string | null;
  /** #21 HS Code. */
  hsCode: string | null;
  /** #22 Composition: the washing and care instructions, as the PIM page maps it (V-12). */
  careInstructions: string | null;
  /** #23 SupModel: the brand's style code. */
  supModel: string | null;
  /** #26 SupColor: the colour as the brand names it. */
  supColor: string | null;
  /** #28 Title: the style name in full. */
  title: string | null;
  /** #29 MID CODE of the manufacturer of the SKU (C-29). */
  midCode: string | null;
}

type ItemField = keyof ErpItem;

interface Column {
  /** The name of the column on the PIM page. */
  name: string;
  /** The field it holds; null for a column of TLG's PIM that Tomorrow leaves empty. */
  field: ItemField | null;
  /** The longest value; null for `NVARCHAR` and the numbers. */
  max: number | null;
  /** Required on the PIM page: `attributes` only from the send of the attributes (the EAN, D-22). */
  required?: true | "attributes";
  /** A descriptive column a longer value is cut to, with a warning on the row; any other is a row error (§7.3.3). */
  truncate?: true;
  number?: true;
}

/** The 30 columns, in their fixed order (§7.3.2). */
export const ERP_ITEM_COLUMNS: readonly Column[] = [
  { name: "Product ID", field: "productId", max: 20, required: true },
  { name: "EAN CODE", field: "ean", max: 20, required: "attributes" },
  { name: "Season", field: "season", max: 20 },
  { name: "MODEL", field: "model", max: 20 },
  { name: "FABRIC", field: null, max: 20 },
  { name: "VARIANT", field: null, max: 20 },
  { name: "COLOR", field: "color", max: 20 },
  { name: "SIZE", field: "size", max: 20 },
  { name: "SEX", field: "sex", max: 20 },
  { name: "Brand Name", field: "brandName", max: 20, truncate: true },
  { name: "Brand Code", field: "brandCode", max: 2, required: true },
  { name: "Wholesale Price", field: "wholesalePrice", max: null, number: true },
  { name: "Product Type", field: "productType", max: 20, required: true },
  { name: "Made In", field: "madeIn", max: 20 },
  { name: "Materials", field: "materials", max: null },
  { name: "Product description", field: "description", max: 30, required: true, truncate: true },
  { name: "Category", field: "category", max: 30 },
  { name: "Sub-category", field: "subcategory", max: 30 },
  { name: "Supplier Name", field: "supplierName", max: 40 },
  { name: "Supplier Code", field: "supplierCode", max: 20 },
  { name: "HS Code", field: "hsCode", max: 20 },
  { name: "Composition", field: "careInstructions", max: 50 },
  { name: "SupModel", field: "supModel", max: 20 },
  { name: "SupFabric", field: null, max: 20 },
  { name: "SupVariant", field: null, max: 20 },
  { name: "SupColor", field: "supColor", max: 20, truncate: true },
  { name: "MFCSDVT", field: null, max: 50 },
  { name: "Title", field: "title", max: 50, truncate: true },
  { name: "MID CODE", field: "midCode", max: 50 },
  { name: "SAT code", field: null, max: 50 },
];

/** The separator of the columns, and the end of every row (§7.3.1, V-13). */
const SEPARATOR = ";";
const LINE_END = "\r\n";

// --- From the data of Nexxt to the row: the codes of Business Central through the mappings, as I-01 and I-22 ---

/** A SKU as the catalog reads it, in the context of a linesheet: reference data by Nexxt id. */
export interface ErpItemSource {
  skuCode: string | null;
  ean: string | null;
  seasonId: string;
  itemCode: string | null;
  colourCode: string | null;
  sizeId: string;
  genderId: string | null;
  brandOrgId: string;
  brandName: string;
  wholesalePrice: string | null;
  productType: string | null;
  countryOfOrigin: string | null;
  materials: string | null;
  styleName: string | null;
  productCategoryId: string | null;
  productSubcategoryId: string | null;
  supplierOrgId: string | null;
  supplierName: string | null;
  hsCode: string | null;
  careInstructions: string | null;
  styleCode: string;
  colourDescription: string | null;
  midCode: string | null;
}

/**
 * The mappings the file may lack: a supplier without the number of Business Central leaves #20 empty (§7.3.2). Every
 * other code without a mapping stops the send and names the data (§7.3.3, spec v0.2 §4 rule 4).
 */
export const ERP_ITEM_OPTIONAL_CODES: readonly string[] = ["supplier"];

/**
 * The row of a SKU, with the codes of Business Central the translator gives: run once to record what the row asks
 * for, then with what `platform.code_mapping` holds (as the translators of TIL, `til-translation.ts`).
 */
export function erpItemOf(source: ErpItemSource, t: Translator): ErpItem {
  const code = (domain: string, id: string | null) => (id === null ? null : t.code(domain, id) || null);
  return {
    productId: source.skuCode,
    ean: source.ean,
    season: code("season", source.seasonId),
    model: source.itemCode,
    color: source.colourCode,
    size: code("size", source.sizeId),
    sex: code("gender", source.genderId),
    brandName: source.brandName,
    brandCode: code("brand", source.brandOrgId),
    wholesalePrice: source.wholesalePrice,
    productType: source.productType,
    madeIn: source.countryOfOrigin,
    materials: source.materials,
    description: source.styleName,
    category: code("product_category", source.productCategoryId),
    subcategory: code("product_subcategory", source.productSubcategoryId),
    supplierName: source.supplierName,
    supplierCode: code("supplier", source.supplierOrgId),
    hsCode: source.hsCode,
    careInstructions: source.careInstructions,
    supModel: source.styleCode,
    supColor: source.colourDescription,
    title: source.styleName,
    midCode: source.midCode,
  };
}

// --- Validation before the send (§7.3.3): nothing is left out in silence ---

/** What a row says of each column: the cleaned value, or why it cannot go. */
interface Cell {
  column: Column;
  value: string;
  problem: "required" | "too_long" | "truncated" | null;
}

/**
 * A value as the file holds it: no `;`, line break or tab, which would break the row (no value is quoted, §7.3.1),
 * and no spaces around it.
 */
export const cleanValue = (value: string): string => value.replace(/[;\r\n\t]/g, " ").trim();

/** Characters, not UTF-16 units: `VARCHAR(n)` counts characters. */
const chars = (value: string) => Array.from(value);

/** `1234.5` → `1234,50`: two decimals with a comma, no thousands separator (§7.3.1, V-13). */
export const fileNumber = (value: string): string => new Big(value).toFixed(2).replace(".", ",");

function cellsOf(item: ErpItem, send: ErpItemSend): Cell[] {
  return ERP_ITEM_COLUMNS.map((column) => {
    const raw = column.field === null ? null : item[column.field];
    const value = raw === null ? "" : column.number ? fileNumber(raw) : cleanValue(raw);
    const required = column.required === true || (column.required === "attributes" && send === "attributes");
    if (value === "") return { column, value, problem: required ? "required" : null };
    if (column.max === null || chars(value).length <= column.max) return { column, value, problem: null };
    if (!column.truncate) return { column, value, problem: "too_long" };
    return { column, value: chars(value).slice(0, column.max).join("").trimEnd(), problem: "truncated" };
  });
}

/** A SKU to check or to write: how the user knows its row, and its values. */
export interface ErpItemRow {
  row: number;
  item: ErpItem;
}

export interface ErpItemCheck {
  /** What stops the row: a required column empty, a value longer than its column (`erp.<field>`, value: the SKU). */
  issues: ValidationIssue[];
  /** The descriptive values the file cuts to their column (§7.3.3): the row goes, and says it. */
  truncated: ValidationIssue[];
}

const issueOf = (cell: Cell, row: ErpItemRow): ValidationIssue => ({
  row: row.row,
  field: `erp.${cell.column.field ?? cell.column.name}`,
  rule: cell.problem ?? "invalid_value",
  ...(row.item.productId !== null && { value: row.item.productId.slice(0, 200) }),
});

/** Every problem of the rows of a send at once: the send is refused while one row has an issue. */
export function checkErpItems(rows: readonly ErpItemRow[], send: ErpItemSend): ErpItemCheck {
  const issues: ValidationIssue[] = [];
  const truncated: ValidationIssue[] = [];
  for (const row of rows) {
    for (const cell of cellsOf(row.item, send)) {
      if (cell.problem === "truncated") truncated.push(issueOf(cell, row));
      else if (cell.problem !== null) issues.push(issueOf(cell, row));
    }
  }
  return { issues, truncated };
}

/**
 * The text of the row of a SKU: 30 columns, 29 separators, the descriptive values cut to their column. Two rows are
 * the same text exactly when Business Central would get the same item, so the catalog keeps the text it sent to know
 * a SKU changed since (§7.3.4). A row with an issue (`checkErpItems`) never goes in a file.
 */
export function erpItemLine(item: ErpItem): string {
  return cellsOf(item, "linesheet")
    .map((cell) => cell.value)
    .join(SEPARATOR);
}

// --- The files ---

const two = (n: number) => String(n).padStart(2, "0");

/**
 * `PRODUCTS_YYYYMMDD_HHMMSS.csv` at `at` in UTC (§7.3.1, V-13); from the second file of the same second on,
 * `_<n>` before the extension, so no two files of a company share a name. The company is the folder (V-14).
 */
export function erpItemFileName(at: Date, n = 1): string {
  const day = `${at.getUTCFullYear()}${two(at.getUTCMonth() + 1)}${two(at.getUTCDate())}`;
  const time = `${two(at.getUTCHours())}${two(at.getUTCMinutes())}${two(at.getUTCSeconds())}`;
  return `PRODUCTS_${day}_${time}${n > 1 ? `_${n}` : ""}.csv`;
}

export interface ErpItemFile {
  name: string;
  /** UTF-8 without BOM once written to bytes (V-13): no header, a CRLF after every row. */
  content: string;
  /** How many rows of the input it holds, in order from where the file before it ended. */
  rows: number;
}

/**
 * The files of the rows, in their order, at most `ERP_ITEM_FILE_MAX_ROWS` each. `taken` is how many files of the
 * same company already have the name of `at`: the new ones follow them.
 */
export function erpItemFiles(lines: readonly string[], at: Date, taken = 0): ErpItemFile[] {
  const files: ErpItemFile[] = [];
  for (let from = 0; from < lines.length; from += ERP_ITEM_FILE_MAX_ROWS) {
    const rows = lines.slice(from, from + ERP_ITEM_FILE_MAX_ROWS);
    files.push({
      name: erpItemFileName(at, taken + files.length + 1),
      content: rows.map((line) => `${line}${LINE_END}`).join(""),
      rows: rows.length,
    });
  }
  return files;
}
